import { ChangeEvent, FC, useState } from 'react'
import { Modal, Form, Input, Select, Button, notification, Upload, Spin } from 'antd'
import { UploadOutlined } from '@ant-design/icons'
import { useTranslation } from 'react-i18next'

import { BlockTrainFileType, BlockTrainStatusEnum } from '@types'
import { STATUS_TRANSITIONS } from '@constants'

import useBlockTrainFiles from '../../../hooks/blocktrain/useBlockTrainFiles'

interface AddBlockTrainFileModalProps {
  visible: boolean
  onCancel: () => void
  onCreate: (values: any) => void
  blockTrainStatus: BlockTrainStatusEnum
  blockTrainId: number
  fileTypes?: BlockTrainFileType[]
}

const AddBlockTrainFileModal: FC<AddBlockTrainFileModalProps> = ({
  visible,
  onCancel,
  onCreate,
  blockTrainId,
  fileTypes,
  blockTrainStatus,
}) => {
  const { t } = useTranslation()
  const [form] = Form.useForm()
  const [noteContent, setNoteContent] = useState<string>('')
  const [selectedFileTypeId, setSelectedFileTypeId] = useState<number | undefined>()

  const [fileList, setFileList] = useState<any[]>([])
  const { uploadBlocktrainFileIsFetching, uploadBlocktrainFile } = useBlockTrainFiles()

  const handleSetNoteContent = (e: ChangeEvent<HTMLTextAreaElement>) => {
    setNoteContent(e.target.value)
  }

  const handleFileTypeChange = (fileTypeId: number | undefined) => {
    setSelectedFileTypeId(fileTypeId)
    form.setFieldsValue({ fileTypeId })
  }

  const handleUploadChange = (info: any) => {
    let newFileList = [...info.fileList]

    newFileList = newFileList.slice(-1)

    setFileList(newFileList)
  }

  const handleCreateFile = async (values: any) => {
    try {
      if (fileList.length > 0) {
        const filePayload = {
          blockTrainId,
          blockTrainFile: fileList[0].originFileObj,
          fileTypeId: selectedFileTypeId,
        }

        await uploadBlocktrainFile(filePayload)
        onCreate(values)
        resetForm()
      } else {
        notification.error({
          message: t('globals.file_required'),
        })
      }
    } catch (error) {
      notification.error({
        message: t('globals.error_uploading_file'),
      })
    } finally {
    }
  }

  const resetForm = () => {
    form.resetFields()
    setNoteContent('')
    setFileList([])
  }

  const handleCancel = () => {
    resetForm()
    onCancel()
  }

  return (
    <Modal
      title={t('new_file')}
      open={visible}
      onCancel={handleCancel}
      cancelText={t('cancel')}
      className="entity-modal"
      okText={t('save')}
      onOk={handleCreateFile}
      okButtonProps={{ disabled: !!uploadBlocktrainFileIsFetching }}
    >
      <Form form={form} layout="horizontal" style={{ marginTop: '1rem' }}>
        <Spin spinning={!!uploadBlocktrainFileIsFetching}>
          <Form.Item
            label={t('file_type')}
            name="fileTypeId"
            style={{ marginBottom: '6px' }}
            rules={[{ required: true, message: t('please_input_PO_number') }]}
          >
            <Select
              onChange={handleFileTypeChange}
              options={fileTypes?.map(fileType => ({
                label: `${fileType.name}  ${
                  fileType.blockingStatus === STATUS_TRANSITIONS[blockTrainStatus]
                    ? `(${t('requiered')})`
                    : ''
                }`,
                value: fileType.id,
              }))}
            />
          </Form.Item>

          <Form.Item label={t('comments_tab')} name="note">
            <Input.TextArea
              onChange={handleSetNoteContent}
              value={noteContent}
              maxLength={1200}
              rows={3}
              allowClear
            />
          </Form.Item>

          <Form.Item
            style={{
              display: 'flex',
              justifyContent: 'flex-end',
              marginBottom: '0.5rem',
            }}
          >
            <Upload onChange={handleUploadChange} fileList={fileList} beforeUpload={() => false}>
              <Button
                icon={<UploadOutlined />}
                style={{ margin: '0.5rem', justifyContent: 'center', width: '10rem' }}
              >
                {t('upload_file')}
              </Button>
            </Upload>
          </Form.Item>
        </Spin>
        <div className="message-modal-file">{t('message_document')}</div>
      </Form>
    </Modal>
  )
}

export default AddBlockTrainFileModal
